'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["disabledPageTooltipTitle", "enabledPageLimit", "isActive", "key", "pageNumber"],
  _excluded2 = ["className", "disabledPageTooltipTitle", "enabledPageLimit", "maxVisiblePageButtons", "onPageChange", "page", "pageCount", "showFirstLastButtons", "showLastPageNumber", "showButtonLabels"];
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PropTypes from 'prop-types';
import { Fragment, PureComponent } from 'react';
import partial from 'react-utils/partial';
import classNames from 'classnames';
import SyntheticEvent from '../core/SyntheticEvent';
import UITooltip from '../tooltip/UITooltip';
import UIButton from '../button/UIButton';
import UIIcon from '../icon/UIIcon';
import Controllable from '../decorators/Controllable';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getMinAndMaxPageNumbers = (maxVisiblePageButtons, page, pageCount) => {
  const buffer = (maxVisiblePageButtons - 1) / 2;
  let min = page - buffer;
  let max = page + buffer;
  const numPagesToShow = Math.min(maxVisiblePageButtons, pageCount);
  if (min < 1) {
    min = 1;
    max = min + maxVisiblePageButtons - 1;
  }
  if (max > pageCount) {
    max = Math.max(pageCount, 1);
    min = max - numPagesToShow + 1;
  }
  return [Math.floor(min), Math.floor(max)];
};
class UIPaginator extends PureComponent {
  constructor(...args) {
    super(...args);
    this.goToPage = page => {
      if (page === this.props.page) return;
      this.props.onPageChange(SyntheticEvent(page));
    };
  }
  renderPageNumber(_ref) {
    let {
        disabledPageTooltipTitle,
        enabledPageLimit,
        isActive,
        key,
        pageNumber
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return /*#__PURE__*/_jsx(UITooltip, {
      disabled: enabledPageLimit == null || enabledPageLimit != null && pageNumber <= enabledPageLimit,
      title: disabledPageTooltipTitle,
      children: /*#__PURE__*/_jsxs(UIButton, Object.assign({}, rest, {
        className: "private-paginator__button private-paginator__button--numeric" + (isActive ? " private-paginator__button--active" : ""),
        "data-page-number": pageNumber,
        disabled: enabledPageLimit != null && pageNumber > enabledPageLimit,
        onClick: partial(this.goToPage, pageNumber),
        responsive: false,
        size: "xs",
        use: "transparent",
        children: [/*#__PURE__*/_jsxs("span", {
          className: "sr-only",
          children: [isActive ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "salesUI.UIPaginator.pageNumber.currentIndicator"
          }) : null, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "salesUI.UIPaginator.pageNumber.description"
          })]
        }), pageNumber]
      }))
    }, key);
  }
  renderNumberedPages(maxVisiblePageButtons, page, pageCount, enabledPageLimit, disabledPageTooltipTitle) {
    const [min, max] = getMinAndMaxPageNumbers(maxVisiblePageButtons, page, pageCount);
    const links = [];
    for (let currentPage = min; currentPage <= max; currentPage++) {
      const isActive = currentPage === page;
      links.push(this.renderPageNumber({
        'aria-current': isActive ? 'true' : null,
        disabledPageTooltipTitle,
        enabledPageLimit,
        isActive,
        key: currentPage - min,
        pageNumber: currentPage
      }));
    }
    return links;
  }
  renderLastPageNumber(maxVisiblePageButtons, page, pageCount, enabledPageLimit, disabledPageTooltipTitle) {
    const [min, max] = getMinAndMaxPageNumbers(maxVisiblePageButtons, page, pageCount);
    if (maxVisiblePageButtons < 1 || pageCount < 1 || pageCount === Infinity || pageCount >= min && pageCount <= max) {
      return null;
    }
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [pageCount !== max + 1 && /*#__PURE__*/_jsx(UIIcon, {
        name: "ellipses",
        size: 22,
        className: "p-all-0 m-y-0 m-x-4 align-self-end"
      }), this.renderPageNumber({
        disabledPageTooltipTitle,
        enabledPageLimit,
        pageNumber: pageCount
      })]
    });
  }
  render() {
    const _this$props = this.props,
      {
        className,
        disabledPageTooltipTitle,
        enabledPageLimit,
        maxVisiblePageButtons,
        page,
        pageCount,
        showFirstLastButtons,
        showLastPageNumber,
        showButtonLabels
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded2);
    const arrowButtonClassName = showButtonLabels ? '' : 'sr-only';
    return /*#__PURE__*/_jsxs("nav", Object.assign({
      "aria-label": I18n.text('salesUI.UIPaginator.label'),
      className: classNames('private-paginator', className),
      "data-current-page": page,
      role: "navigation"
    }, rest, {
      children: [/*#__PURE__*/_jsxs("span", {
        className: "private-paginator__backward-controls",
        children: [showFirstLastButtons ? /*#__PURE__*/_jsxs(UIButton, {
          "aria-label": I18n.text('salesUI.UIPaginator.firstPage.description'),
          className: "private-paginator__button",
          "data-first-page": true,
          disabled: page < 2,
          onClick: partial(this.goToPage, 1),
          responsive: false,
          size: "xs",
          use: "transparent",
          children: [/*#__PURE__*/_jsx(UIIcon, {
            className: "private-paginator__icon",
            name: "first"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "salesUI.UIPaginator.firstPage.label",
            className: arrowButtonClassName
          })]
        }, "first") : null, /*#__PURE__*/_jsxs(UIButton, {
          "aria-label": I18n.text('salesUI.UIPaginator.previousPage.description'),
          className: "private-paginator__button",
          "data-prev-page": true,
          disabled: page < 2,
          onClick: partial(this.goToPage, Math.max(page - 1, 1)),
          responsive: false,
          size: "xs",
          use: "transparent",
          children: [/*#__PURE__*/_jsx(UIIcon, {
            className: "private-paginator__icon",
            name: "left",
            size: "xs"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "salesUI.UIPaginator.previousPage.label",
            className: arrowButtonClassName
          })]
        }, "prev")]
      }), /*#__PURE__*/_jsxs("span", {
        className: "private-paginator__numbers",
        children: [this.renderNumberedPages(maxVisiblePageButtons, page, pageCount, enabledPageLimit, disabledPageTooltipTitle), showLastPageNumber && this.renderLastPageNumber(maxVisiblePageButtons, page, pageCount, enabledPageLimit, disabledPageTooltipTitle)]
      }), /*#__PURE__*/_jsxs("span", {
        className: "private-paginator__forward-controls",
        children: [/*#__PURE__*/_jsxs(UIButton, {
          "aria-label": I18n.text('salesUI.UIPaginator.nextPage.description'),
          className: "private-paginator__button",
          "data-next-page": true,
          disabled: page >= pageCount || enabledPageLimit != null && page >= enabledPageLimit,
          onClick: partial(this.goToPage, Math.min(page + 1, pageCount)),
          responsive: false,
          size: "xs",
          use: "transparent",
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "salesUI.UIPaginator.nextPage.label",
            className: arrowButtonClassName
          }), /*#__PURE__*/_jsx(UIIcon, {
            className: "private-paginator__icon",
            name: "right",
            size: "xs"
          })]
        }, "next"), showFirstLastButtons ? /*#__PURE__*/_jsxs(UIButton, {
          "aria-label": I18n.text('salesUI.UIPaginator.lastPage.description'),
          className: "private-paginator__button",
          "data-last-page": true,
          disabled: enabledPageLimit != null ? page >= enabledPageLimit : pageCount === page,
          onClick: partial(this.goToPage, enabledPageLimit != null ? enabledPageLimit : pageCount),
          responsive: false,
          size: "xs",
          use: "transparent",
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "salesUI.UIPaginator.lastPage.label",
            className: arrowButtonClassName
          }), /*#__PURE__*/_jsx(UIIcon, {
            className: "private-paginator__icon",
            name: "last"
          })]
        }, "last") : null]
      })]
    }));
  }
}
UIPaginator.displayName = 'UIPaginator';
UIPaginator.defaultProps = {
  maxVisiblePageButtons: 11,
  showFirstLastButtons: false,
  showButtonLabels: true,
  page: 1
};
UIPaginator.propTypes = {
  className: PropTypes.string,
  disabledPageTooltipTitle: PropTypes.node,
  maxVisiblePageButtons: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  enabledPageLimit: PropTypes.number,
  /*
   * Show buttons for navigating to the first and
   * last pages. Requires a finite positive `pageCount`.
   */
  showFirstLastButtons: PropTypes.bool.isRequired,
  /*
   * Show the last page number after an ellipsis.
   * Requires a finite positive `pageCount`.
   */
  showLastPageNumber: PropTypes.bool,
  showButtonLabels: PropTypes.bool.isRequired
};
export default Controllable(UIPaginator, ['page']);